const API_BASE = (import.meta as any).env?.VITE_API_BASE || ''

export function getApiBase() {
  return API_BASE
}

let keyHash: string | null = null

export function getKeyHash(): string | null {
  if (keyHash) return keyHash
  const stored = localStorage.getItem('bearing_key_hash')
  if (stored) {
    keyHash = stored
    return stored
  }
  return null
}

export function setKeyHash(h: string) {
  keyHash = h
  localStorage.setItem('bearing_key_hash', h)
}

export function clearKey() {
  keyHash = null
  localStorage.removeItem('bearing_key_hash')
  localStorage.removeItem('bearing_circle')
}

export function getCircle(): { id: string; code: string; name: string; pseudonym: string } | null {
  const raw = localStorage.getItem('bearing_circle')
  return raw ? JSON.parse(raw) : null
}

export function setCircle(c: any) {
  localStorage.setItem('bearing_circle', JSON.stringify(c))
}

export function clearCircle() {
  localStorage.removeItem('bearing_circle')
}

export async function api(path: string, init?: RequestInit) {
  const headers: any = {
    'Content-Type': 'application/json',
    ...(init?.headers || {}),
  }
  const kh = getKeyHash()
  if (kh) headers['Authorization'] = `Aegis ${kh}`

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || `http_${res.status}`)
  }
  return res.json()
}

export async function identify(key: string, turnstileToken?: string) {
  return api('/api/identify', {
    method: 'POST',
    body: JSON.stringify({ keyHash: key, turnstileToken }),
  })
}

export async function createCircle(name: string, turnstileToken?: string) {
  return api('/api/circles', {
    method: 'POST',
    body: JSON.stringify({ name, turnstileToken }),
  })
}

export async function joinCircle(code: string, pseudonym?: string, turnstileToken?: string) {
  return api('/api/circles/join', {
    method: 'POST',
    body: JSON.stringify({ code: code.toUpperCase(), pseudonym, turnstileToken }),
  })
}

export async function createObservation(payload: any) {
  return api('/api/observations', { method: 'POST', body: JSON.stringify(payload) })
}

export async function listObservations(circleId?: string, mine?: boolean) {
  const q = circleId ? `?circle=${circleId}` : mine ? '?mine=1' : ''
  return api(`/api/observations${q}`)
}

export async function getObservation(id: string) {
  return api(`/api/observations/${id}`)
}

export async function addPerspective(obsId: string, role: string, text: string, isGuest?: boolean, turnstileToken?: string) {
  return api(`/api/observations/${obsId}/perspectives`, {
    method: 'POST',
    body: JSON.stringify({ role, text, isGuest, turnstileToken }),
  })
}

export async function restoreOrientation(obsId: string, steadier: string, remainsOpen: string, microShift: string) {
  return api(`/api/observations/${obsId}/restore`, {
    method: 'POST',
    body: JSON.stringify({ steadier, remainsOpen, microShift }),
  })
}

export async function linkTo(obsId: string, payload: { targetObsId?: string; currentId?: string; note?: string }) {
  return api(`/api/observations/${obsId}/link`, { method: 'POST', body: JSON.stringify(payload) })
}

export async function listCurrents(circleId: string) {
  return api(`/api/currents?circle=${circleId}`)
}

export async function createCurrent(circleId: string, name: string, description?: string) {
  return api('/api/currents', { method: 'POST', body: JSON.stringify({ circleId, name, description }) })
}

export async function attachToCurrent(currentId: string, obsIds: string[]) {
  return api(`/api/currents/${currentId}/attach`, { method: 'POST', body: JSON.stringify({ obsIds }) })
}

export async function getLoom(currentId: string) {
  return api(`/api/currents/${currentId}/loom`)
}

export async function getPublicCircle(code: string) {
  return fetch(`${getApiBase()}/api/circles/${code}/public`).then(r => r.json())
}

export async function getMyOrientations() {
  return api('/api/me/orientations')
}

export async function aiSurfaceAssumptions(description: string, explanation?: string) {
  return api('/api/ai/surface-assumptions', { method: 'POST', body: JSON.stringify({ description, explanation }) })
}

export async function aiPerspectivePrompts(situation: string, role: string) {
  return api('/api/ai/perspective-prompts', { method: 'POST', body: JSON.stringify({ situation, role }) })
}

export async function aiResonanceCandidates(description: string, salienceTags: string[], candidates: any[]) {
  return api('/api/ai/resonance-candidates', { method: 'POST', body: JSON.stringify({ description, salienceTags, candidates }) })
}

export async function requestExport(kind: string, circleId?: string, currentId?: string) {
  return api('/api/export', { method: 'POST', body: JSON.stringify({ kind, circleId, currentId }) })
}

export function openWs(circleId: string, onMessage: (data: any) => void): WebSocket | null {
  const base = API_BASE.replace(/^http/, 'ws')
  try {
    const ws = new WebSocket(`${base}/ws/circle/${circleId}`)
    ws.onmessage = (ev) => {
      try { onMessage(JSON.parse(ev.data)) } catch {}
    }
    ws.onclose = () => { /* silent */ }
    return ws
  } catch {
    return null
  }
}

// Turnstile loader (lazy)
let turnstileLoaded = false
export function loadTurnstile() {
  if (turnstileLoaded || (window as any).turnstile) return Promise.resolve()
  return new Promise<void>((resolve) => {
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    s.onload = () => { turnstileLoaded = true; resolve() }
    document.head.appendChild(s)
  })
}

export function renderTurnstile(container: HTMLElement, sitekey: string, callback: (token: string) => void) {
  const w = (window as any).turnstile
  if (!w) return
  w.render(container, {
    sitekey,
    callback,
    theme: 'light',
    size: 'normal',
  })
}
